import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertTriangle, CheckCircle2, CircleHelp, Clock3, ExternalLink,
  Lightbulb, RefreshCw, ShieldCheck, ThumbsUp, XCircle,
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

const severityMeta = {
  high: { label: "Hohe Priorität", card: "border-red-200 bg-red-50", badge: "bg-red-100 text-red-800", icon: "text-red-600" },
  medium: { label: "Prüfen", card: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-800", icon: "text-amber-600" },
  low: { label: "Hinweis", card: "border-sky-200 bg-sky-50", badge: "bg-sky-100 text-sky-800", icon: "text-sky-600" },
};

const money = (value, currency) => {
  if (!value || !currency) return null;
  try { return new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(value); }
  catch { return `${Number(value).toLocaleString("de-DE")} ${currency}`; }
};

export function BusinessGuardianCard({ setTab }) {
  const [summary, setSummary] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState("");

  const headers = useCallback(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const load = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const [summaryResponse, signalResponse] = await Promise.all([
        axios.get(`${API}/api/guardian/summary`, { headers: headers() }),
        axios.get(`${API}/api/guardian/signals`, { headers: headers(), params: { limit: 6, refresh: true } }),
      ]);
      setSummary(summaryResponse.data);
      setSignals(signalResponse.data.signals || []);
    } catch (err) {
      console.error(err);
      setError("Guardian-Hinweise konnten gerade nicht geladen werden.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const potentialLabels = useMemo(() => Object.entries(summary?.potential_review_value_by_currency || {})
    .map(([currency, value]) => money(value, currency)).filter(Boolean), [summary]);

  const giveFeedback = async (action) => {
    if (!selected || actionBusy) return;
    setActionBusy(true);
    try {
      await axios.post(`${API}/api/guardian/signals/${selected.id}/feedback`,
        { action, snooze_days: action === "snooze" ? 7 : undefined },
        { headers: headers() },
      );
      if (["not_relevant", "hide_case", "snooze", "resolved"].includes(action)) setSelected(null);
      await load(true);
    } catch (err) {
      console.error(err);
      setError("Die Rückmeldung konnte nicht gespeichert werden.");
    } finally {
      setActionBusy(false);
    }
  };

  const goToSource = () => {
    if (!selected?.target?.tab || !setTab) return;
    setTab(selected.target.tab);
    setSelected(null);
  };

  if (loading) {
    return <Card className="p-5"><p className="text-sm text-gray-400">Business Guardian wird geladen …</p></Card>;
  }

  return (
    <>
      <Card className="p-5 border-[var(--brand-border)]" data-testid="business-guardian-card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[var(--brand-primary)]" />
              <h3 className="font-semibold text-[var(--brand-ink)]">Business Guardian</h3>
            </div>
            <p className="mt-1 text-sm text-[var(--brand-muted)]">
              Prüft vorhandene Daten auf fehlende nächste Schritte, Fristen und Unterlagen. Es werden keine Aktionen automatisch ausgeführt.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={refreshing}>
            <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Aktualisieren
          </Button>
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg bg-[var(--brand-bg)] p-3">
            <p className="text-xs text-[var(--brand-muted)]">Offene Prüfhinweise</p>
            <p className="mt-1 text-2xl font-bold text-[var(--brand-ink)]">{summary?.open_count || 0}</p>
          </div>
          <div className="rounded-lg bg-[var(--brand-bg)] p-3">
            <p className="text-xs text-[var(--brand-muted)]">Hohe Priorität</p>
            <p className="mt-1 text-2xl font-bold text-red-600">{summary?.by_severity?.high || 0}</p>
          </div>
          <div className="rounded-lg bg-[var(--brand-bg)] p-3">
            <p className="text-xs text-[var(--brand-muted)]">Prüfwert aus zugeordneten Fällen</p>
            <p className="mt-1 text-sm font-semibold text-[var(--brand-ink)]">{potentialLabels.length ? potentialLabels.join(" · ") : "Noch kein Betrag"}</p>
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {signals.length === 0 ? (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              Aktuell erkennt der Guardian keine offenen Prüfhilfen auf Basis deiner vorhandenen Daten.
            </div>
          ) : signals.map((signal) => {
            const meta = severityMeta[signal.severity] || severityMeta.low;
            const amount = money(signal.economic_value, signal.currency);
            return (
              <button
                type="button"
                key={signal.id}
                onClick={() => setSelected(signal)}
                className={`block w-full rounded-lg border p-3 text-left ${meta.card} focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]`}
                data-testid={`guardian-signal-${signal.id}`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${meta.icon}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-[var(--brand-ink)]">{signal.title}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}>{meta.label}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{signal.description}</p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
                      {signal.due_date && <span>Frist: {signal.due_date}</span>}
                      {amount && <span>Prüfwert: {amount}</span>}
                      <span className="inline-flex items-center gap-1"><CircleHelp className="h-3 w-3" /> Warum sehe ich das?</span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-[var(--brand-muted)]">{summary?.disclaimer}</p>
      </Card>

      <Sheet open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg">
          {selected && (() => {
            const meta = severityMeta[selected.severity] || severityMeta.low;
            const amount = money(selected.economic_value, selected.currency);
            const why = selected.why || {};
            return (
              <>
                <SheetHeader>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className={`h-5 w-5 ${meta.icon}`} />
                    <SheetTitle>{selected.title}</SheetTitle>
                  </div>
                  <SheetDescription>{selected.description}</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-5">
                  <section className="rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] p-4">
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-ink)]"><CircleHelp className="h-4 w-4" /> Warum sehe ich das?</h4>
                    <p className="mt-2 text-sm text-gray-700">{why.rule || "Der Hinweis wurde aus vorhandenen Geschäftsdaten abgeleitet."}</p>
                    {why.data_window && <p className="mt-2 text-xs text-gray-500">Betrachteter Zeitraum: {why.data_window}</p>}
                    {Array.isArray(why.assumptions) && why.assumptions.length > 0 && (
                      <div className="mt-3 rounded-md bg-white p-3 text-xs text-gray-600">
                        <p className="font-medium text-gray-700">Wichtige Einordnung</p>
                        {why.assumptions.map((assumption, index) => <p key={index} className="mt-1">{assumption}</p>)}
                      </div>
                    )}
                  </section>

                  <section className="space-y-2">
                    <h4 className="text-sm font-semibold text-[var(--brand-ink)]">Grundlage</h4>
                    {(selected.sources || []).length ? selected.sources.map((source, index) => (
                      <div key={`${source.collection}-${source.id || index}`} className="rounded-md border border-[var(--brand-border)] p-3 text-sm text-gray-700">
                        {source.label || source.collection}
                      </div>
                    )) : <p className="text-sm text-gray-500">Keine Quelle verfügbar.</p>}
                    {amount && <p className="text-sm text-gray-600">Prüfwert: <strong>{amount}</strong></p>}
                  </section>

                  <section>
                    <h4 className="mb-2 text-sm font-semibold text-[var(--brand-ink)]">Was möchtest du tun?</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {selected.target?.tab && <Button onClick={goToSource} className="bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary-dark)]"><ExternalLink className="mr-2 h-4 w-4" />Zum betroffenen Bereich</Button>}
                      <Button variant="outline" onClick={() => giveFeedback("helpful")} disabled={actionBusy}><ThumbsUp className="mr-2 h-4 w-4" />Hinweis ist hilfreich</Button>
                      <Button variant="outline" onClick={() => giveFeedback("snooze")} disabled={actionBusy}><Clock3 className="mr-2 h-4 w-4" />In 7 Tagen wieder zeigen</Button>
                      <Button variant="outline" onClick={() => giveFeedback("not_relevant")} disabled={actionBusy}><XCircle className="mr-2 h-4 w-4" />Für diesen Fall nicht relevant</Button>
                    </div>
                    <p className="mt-3 text-xs text-[var(--brand-muted)]"><Lightbulb className="mr-1 inline h-3 w-3" />Diese Auswahl verändert keine Rechnung, keine Zahlung und keinen Vertrag. Sie speichert nur deine Einschätzung zu diesem Hinweis.</p>
                  </section>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </>
  );
}
